import Parseable from '@/models/Parseable';
import Customer from '@/models/Customer';
import Actuator from '@/models/Actuator';
import Revision, { revisionStandLabel } from '@/models/Revision';
import ContactPerson from '@/models/ContactPerson';
import { isQuotationCountry, QuotationCountry } from '@/constants/quotationCountries';
import { isQuotationType, QuotationType } from '@/constants/quotationTypes';
import { normalizeCountryCode } from '@/constants/countries';
import { parseOfferVolumeAmount } from '@/utils/offerVolumeFormat';

/** Bearbeitungsstatus der Anfrage */
export type ProjectStatus = 'neu' | 'in_bearbeitung' | 'warten' | 'fertig' | 'gewonnen' | 'verloren';

export const PROJECT_STATUSES: Array<{
    value: ProjectStatus;
    label: string;
    color: string;
    hint: string;
}> = [
    {
        value: 'neu',
        label: 'Neu',
        color: 'amber darken-2',
        hint: 'Anfrage ist neu eingegangen und muss bearbeitet werden.'
    },
    {
        value: 'in_bearbeitung',
        label: 'In Bearbeitung',
        color: 'teal',
        hint: 'Angebot wird gerade bearbeitet.'
    },
    {
        value: 'warten',
        label: 'Warten',
        color: 'light-blue darken-1',
        hint: 'Warten auf Rückmeldung oder weitere Informationen.'
    },
    {
        value: 'fertig',
        label: 'Fertig',
        color: 'deep-purple darken-1',
        hint: 'Angebot abgeschlossen (Summe erfasst).'
    },
    {
        value: 'gewonnen',
        label: 'Gewonnen',
        color: 'green darken-2',
        hint: 'Auftrag gewonnen.'
    },
    {
        value: 'verloren',
        label: 'Verloren',
        color: 'red darken-2',
        hint: 'Angebot verloren oder nicht weiterverfolgt.'
    }
];

export function projectStatusValue(project?: Partial<Project> | null): ProjectStatus {
    const raw = project?.projectStatus;
    if (raw && PROJECT_STATUSES.some((entry) => entry.value === raw)) {
        return raw as ProjectStatus;
    }
    return 'neu';
}

export function projectStatusLabel(status?: string | null): string {
    return PROJECT_STATUSES.find((entry) => entry.value === status)?.label || 'Neu';
}

export function projectStatusColor(status?: string | null): string {
    return PROJECT_STATUSES.find((entry) => entry.value === status)?.color || 'amber darken-2';
}

/** Stadium der Anfrage, unabhängig vom Revisionsindex. */
export type ProjectStage = '' | 'feet' | 'budget' | 'purchase';

export const PROJECT_STAGES: Array<{ value: ProjectStage; label: string; color: string }> = [
    { value: '', label: '–', color: 'grey lighten-2' },
    { value: 'feet', label: 'Feet', color: 'orange' },
    { value: 'budget', label: 'Budget', color: 'blue' },
    { value: 'purchase', label: 'Purchase', color: 'green' }
];

export const STAGE_RANK: Record<string, number> = { feet: 1, budget: 2, purchase: 3 };

export function stageColor(stage?: string | null): string {
    return PROJECT_STAGES.find((entry) => entry.value === (stage || ''))?.color || 'grey lighten-2';
}

export type OfferRound = {
    archivedAt?: number;
    projectStatus?: string;
    dueDate?: number | null;
    offerPhase?: string;
    offerCreatedAt?: number | null;
    offerSentAt?: number | null;
    offerVolume?: number | null;
    offerValidFrom?: number | null;
    offerRevisionId?: string | null;
    offerRevisionStand?: string | null;
    followUpOutcome?: string;
    followUpNote?: string | null;
    followUpAt?: number | null;
};

export function stageLabel(stage?: string | null): string {
    return PROJECT_STAGES.find((entry) => entry.value === (stage || ''))?.label || '–';
}

/** Overview payload may ship `revisionStand` instead of full revision lists. */
export function projectRevisionStandLabel(project?: Partial<Project> | null): string {
    if (!project) {
        return '';
    }
    if (project.revisionStand) {
        return project.revisionStand;
    }
    return revisionStandLabel(project.revisions);
}

/**
 * Current stage of a project. Same rule as the backend funnel: Project.stage once it has been set,
 * otherwise the highest tag of the legacy revisions.
 */
export function projectStage(project?: Partial<Project> | null): ProjectStage {
    if (!project) {
        return '';
    }
    if (project.stage || (project.stageHistory && project.stageHistory.length > 0)) {
        return project.stage || '';
    }
    const overviewTag = project.revisionMaxTag;
    if (overviewTag && STAGE_RANK[overviewTag] !== undefined) {
        return overviewTag as ProjectStage;
    }
    return (project.revisions || []).reduce<ProjectStage>((best, revision) =>
        (STAGE_RANK[revision.tag || ''] || 0) > (STAGE_RANK[best] || 0) ? revision.tag as ProjectStage : best, '');
}

export default class Project extends Parseable {

    public id!: string;
    public createdAt!: string | number;
    public name!: string;
    public anfrageNr!: string;
    public customerRequestNumber?: string;
    public angebotsNr!: string;
    public quotationCountry?: QuotationCountry;
    /** ISO 3166-1 alpha-2 (z. B. DE) – Final Destination, nicht Angebotsland. */
    public finalDestination?: string;
    public quotationType?: QuotationType;
    public actuators: Actuator[] = [];
    public actuatorCount?: number;
    public revisions: Revision[] = [];
    /** Set by GET /project/overview (no full revision list). */
    public revisionStand?: string;
    public revisionChangeCount?: number;
    public revisionMaxTag?: ProjectStage | string;
    public customer!: Customer;
    public valveMaker?: Customer | { id?: string; name?: string } | string;
    public endUser?: Customer | { id?: string; name?: string } | string | null;
    /** EPC: Kundenstamm vom Typ Procurement Construction (PC) */
    public epc?: Customer | { id?: string; name?: string } | string | null;
    public contactPerson?: ContactPerson | string;
    public createdBy?: string;
    public alsoAt?: Array<{ id: string; name: string; projectId: string }>;
    /** Stamm-Projekt-ID bei Mehrfach-Kundenzuordnung */
    public linkedProjectId?: string | null;
    public projectStatus?: ProjectStatus;
    public lostReasonCode?: string | null;
    public lostReasonCustom?: string | null;
    public stage?: ProjectStage;
    public stageChangedAt?: number | null;
    public stageHistory?: Array<{ stage: ProjectStage; at: number; by?: string }>;
    public dueDate?: number | null;
    /** Tatsächliches Versanddatum des Angebots (Unix-ms, Legacy) */
    public offerSentAt?: number | null;
    /** Datum „Angebot erstellt“ / Abschluss in Avamo (Unix-ms, Mitternacht lokal) */
    public offerCreatedAt?: number | null;
    public assignee?: string | null;
    /** Angebotsvolumen in EUR */
    public offerVolume?: number | null;
    /** Externer Angebots-Workflow nach Avamo-Abschluss (fertig + Summe) */
    public offerPhase?: '' | 'ready' | 'created' | 'sent' | null;
    /** Nachfassen nach Ablauf der Angebotsgültigkeit */
    public followUpOutcome?: '' | 'needs_time' | 'new_offer' | 'no_new_offer' | null;
    public followUpNote?: string | null;
    /** Zeitpunkt der letzten Nachfass-Notiz (Unix-ms) */
    public followUpAt?: number | null;
    /** Start einer neuen Angebotsgültigkeit nach „Neues Angebot“ (Unix-ms) */
    public offerValidFrom?: number | null;
    /** Revision, deren Aktuatoren die Grundlage des Angebots sind (Arbeitsliste oder -01, -02, …) */
    public offerRevisionId?: string | null;
    /** Stand dieser Revision im Overview, wenn die Revisionsliste nicht mitkommt ("00", "02", …) */
    public offerRevisionStand?: string | null;
    /** Abgelegte Angebotsrunden vor einer neuen Revision */
    public offerHistory?: OfferRound[];

    /**
     * Turns a plain object to a class object
     * @param object
     */
    public static parseFromObject(object: Partial<Project>): Project {
        const project = new Project();
        Object.assign(project, object);

        if (typeof object.customer === 'object' && object.customer !== null) {
            project.customer = Customer.parseFromObject(object.customer);
        }

        if (typeof object.valveMaker === 'object' && object.valveMaker !== null) {
            project.valveMaker = Customer.parseFromObject(object.valveMaker);
        }

        if (typeof object.endUser === 'object' && object.endUser !== null) {
            project.endUser = Customer.parseFromObject(object.endUser);
        }

        if (typeof object.epc === 'object' && object.epc !== null) {
            project.epc = Customer.parseFromObject(object.epc);
        }

        if (typeof object.contactPerson === 'object' && object.contactPerson !== null) {
            project.contactPerson = ContactPerson.parseFromObject(object.contactPerson);
        }

        project.actuators = Array.isArray(object.actuators)
            ? Actuator.parseFromArray(object.actuators)
            : [];

        project.revisions = Array.isArray(object.revisions)
            ? Revision.parseFromArray(object.revisions)
            : [];

        if (!isQuotationCountry(project.quotationCountry)) {
            project.quotationCountry = undefined;
        }
        if (!isQuotationType(project.quotationType)) {
            project.quotationType = undefined;
        }

        const normalizedDestination = normalizeCountryCode(project.finalDestination);
        if (normalizedDestination) {
            project.finalDestination = normalizedDestination;
        }

        if (Object.prototype.hasOwnProperty.call(object, 'dueDate')) {
            const rawDue = object.dueDate;
            if (rawDue == null || String(rawDue).trim() === '') {
                project.dueDate = null;
            } else {
                const dueDate = Number(rawDue);
                project.dueDate = Number.isFinite(dueDate) ? dueDate : null;
            }
        }

        if (Object.prototype.hasOwnProperty.call(object, 'offerValidFrom')) {
            const rawValidFrom = object.offerValidFrom;
            if (rawValidFrom == null || String(rawValidFrom).trim() === '') {
                project.offerValidFrom = null;
            } else {
                const offerValidFrom = Number(rawValidFrom);
                project.offerValidFrom = Number.isFinite(offerValidFrom) ? offerValidFrom : null;
            }
        }

        if (Object.prototype.hasOwnProperty.call(object, 'offerSentAt')) {
            const rawSent = object.offerSentAt;
            if (rawSent == null || String(rawSent).trim() === '') {
                project.offerSentAt = null;
            } else {
                const offerSentAt = Number(rawSent);
                project.offerSentAt = Number.isFinite(offerSentAt) ? offerSentAt : null;
            }
        }

        if (Object.prototype.hasOwnProperty.call(object, 'offerCreatedAt')) {
            const rawCreated = object.offerCreatedAt;
            if (rawCreated == null || String(rawCreated).trim() === '') {
                project.offerCreatedAt = null;
            } else {
                const offerCreatedAt = Number(rawCreated);
                project.offerCreatedAt = Number.isFinite(offerCreatedAt) ? offerCreatedAt : null;
            }
        }

        if (Object.prototype.hasOwnProperty.call(object, 'offerVolume')) {
            project.offerVolume = parseOfferVolumeAmount(object.offerVolume as string | number | null | undefined);
        }

        return project;
    }

    public static parseFromArray(array: Partial<Project>[]): Project[] {
        if (!Array.isArray(array)) {
            return [];
        }

        return array.map((obj) => Project.parseFromObject(obj));
    }

    parseToObject(): any {
        return { ...this };
    }
}
